import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { AgeGroup } from '../../types/domain';
import {
  GlucoseZone,
  getGlucoseTypeOptions,
  getGlucoseStatusText
} from '../../lib/domain-data';

export default function CheckGlucose() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  const [glucoseValue, setGlucoseValue] = useState<number | null>(100);
  const [glucoseZone, setGlucoseZone] = useState<GlucoseZone | null>(null);
  const [glucoseTypeId, setGlucoseTypeId] = useState<string | null>(null);
  const [completionMessage, setCompletionMessage] = useState<string | null>(null);
  const [injectionDate, setInjectionDate] = useState<string>('');
  const [injectionTime, setInjectionTime] = useState<string>('');
  const [isSavingInjection, setIsSavingInjection] = useState(false);
  const [saveInjectionError, setSaveInjectionError] = useState<string | null>(null);

  useEffect(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    setInjectionDate(local.toISOString().slice(0, 10));
    setInjectionTime(local.toISOString().slice(11, 16));
  }, []);

  useEffect(() => {
    if (profile?.birth_date) {
      const birthDate = new Date(profile.birth_date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      const calculatedGroup: AgeGroup = age < 11 ? '2-10' : '11-plus';
      if (ageGroup !== calculatedGroup) {
        setAgeGroup(calculatedGroup);
      }
    }
  }, [profile, ageGroup]);

  const handleSelectGlucoseType = (id: string) => {
    setGlucoseTypeId(id);
  };

  const handleGlucoseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!value || value <= 0) {
      setGlucoseValue(null);
      setGlucoseZone(null);
      return;
    }
      
    // Set value and determine zone
    setGlucoseValue(value);
    if (value < 70) {
      setGlucoseZone('low');
    } else if (value <= 150) {
      setGlucoseZone('normal');
    } else if (value <= 250) {
      setGlucoseZone('warning');
    } else {
      setGlucoseZone('high');
    }
  };

  const handleGlucoseNext = (needsInjection: boolean) => {
    if (needsInjection) {
      navigate('/inject-insulin', { state: { glucoseValue } });
    } else {
      navigate('/');
    }
  };

  const handleSaveGlucoseOnly = async () => {
    if (!ageGroup || !user || glucoseValue === null) {
      return;
    }

    setIsSavingInjection(true);
    setSaveInjectionError(null);
    try {
      await dataService.addInjection(user.id, {
        date: injectionDate,
        time: injectionTime,
        siteId: null,
        insulinTypeId: null,
        ageGroup,
        glucoseValue: glucoseValue
      });

      setCompletionMessage('Data gula darah berhasil disimpan.');
      setTimeout(() => navigate('/'), 2000);
    } catch {
      setSaveInjectionError('Terjadi kesalahan saat menyimpan data gula darah.');
    } finally {
      setIsSavingInjection(false);
    }
  };

  const renderGlucoseStatus = () => {
    const glucoseStatusText = getGlucoseStatusText(glucoseZone);
    if (!glucoseZone || glucoseValue === null || !glucoseStatusText) {
      return null;
    }
    return (
      <div className={`glucose-status ${glucoseZone}`}>
        <div>{glucoseStatusText}</div>
        <div>{glucoseValue} mg/dL</div>
      </div>
    );
  };

  const options = getGlucoseTypeOptions(ageGroup);

  if (ageGroup === null) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-xl text-gray-600">Menyiapkan data adik...</div>
      </div>
    );
  }

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div
        className="screen-card"
        role="group"
        aria-labelledby="glucose-step-title"
      >
        <div id="glucose-step-title" className="section-title">
          Cek gula darah
        </div>
        <div className="steps">
          <div className="step-pill active">1. Waktu cek</div>
          <div className="step-pill">2. Angka gula</div>
          <div className="step-pill">3. Lanjut?</div>
        </div>

        <div className="subtitle">1. Pilih waktu pemeriksaan</div>
        <div className="option-grid mb-3">
          {options.map(option => (
            <button
              key={option.id}
              type="button"
              className={`chip ${glucoseTypeId === option.id ? 'selected' : ''}`}
              onClick={() => handleSelectGlucoseType(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="subtitle">2. Masukkan hasil gula darah</div>
        <div className="flex flex-col items-center">
          <input
            type="number"
            inputMode="numeric"
            className="value-display w-40 bg-transparent border-b-2 border-slate-200 focus:border-indigo-500 outline-none transition-colors p-0 appearance-none m-0"
            value={glucoseValue ?? ''}
            placeholder="---"
            onChange={handleGlucoseChange}
            aria-label="Masukkan nilai gula darah"
          />
        </div>
        <div className="value-label">mg/dL</div>
        <input
          type="range"
          min={40}
          max={400}
          step={1}
          onChange={handleGlucoseChange}
        />

        {renderGlucoseStatus()}

        <div className="subtitle mt-2">
          3. Perlu lanjut ke suntik insulin?
        </div>
        <div className="next-actions grid grid-cols-2 gap-2">
          <button
            type="button"
            className="btn-secondary w-full md:w-auto"
            onClick={() => handleGlucoseNext(false)}
            aria-label="Tidak lanjut suntik insulin sekarang"
          >
            Batal
          </button>
          <div className="flex flex-col sm:flex-row gap-2 col-span-2 md:col-span-1 md:contents">
            <button
              type="button"
              className="btn-secondary w-full md:w-auto bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800"
              disabled={isSavingInjection || glucoseValue === null}
              onClick={handleSaveGlucoseOnly}
              aria-label="Hanya simpan cek darah saja"
            >
              {isSavingInjection ? 'Menyimpan...' : 'Hanya simpan cek darah saja'}
            </button>
            <button
              type="button"
              className="btn-primary w-full md:w-auto"
              onClick={() => handleGlucoseNext(true)}
              aria-label="Lanjut ke langkah suntik insulin"
            >
              Lanjut suntik
            </button>
          </div>
        </div>
        {saveInjectionError ? (
          <div className="subtitle mt-2 text-red-700 text-center">
            {saveInjectionError}
          </div>
        ) : null}
        {completionMessage ? (
          <div className="completion-message mt-2">{completionMessage}</div>
        ) : null}
      </div>
    </div>
  );
}
